import { cloneDeep } from 'lodash';
import { nameFormatter } from '../shiftRotation/utils';
import { alphabeticalSort, getRegion } from '../utils/helpers';
import { getUpdatedProcessInfoWithAssociatesToFiveSProcessPath } from './view/utils';

export const DEFAULT_SHIFT_ALLOCATION_MODEL = {
  associateInfo: {},
  processInfo: [],
  availableCycles: [],
  availableShifts: [],
  boardEntries: []
};

export const CLUSTER_TYPES = {
  CLUSTER: {
    type: 'SORT_CLUSTER',
    label: 'Cluster',
    isADTA: false
  },
  ADTA: {
    type: 'ADTA_CLUSTER',
    label: 'ADTA',
    isADTA: true
  }
};

export const EMPLOYEE_ICON_COLORS = {
  CLOCKED_IN: '#348118',
  CLOCKED_OUT: '#6E777F',
  RECOMMENDED: '#0085b3',
  ISSUE: '#AD0A30'
};

export const EXCLUDED_FROM_VIOLATIONS = ['Trainee', 'OPS', 'Learning Coordinator', 'Non Scan Stow'];

export const PROCESS_PATHS = {
  INDUCT: 'Induct',
  STOW: 'Stow',
  PICK_TO_BUFFER: 'Pick to Buffer',
  TRAINEE: 'Trainee',
  AUTO_SCAN_INDUCT_LOADER: 'Auto Scan Induct Loader',
  DIVERTER: 'Diverter',
  LABELER: 'Labeler',
  ASML_PUSHER: 'ASML Pusher',
  ASML_INDUCT_LOADER: 'ASML Induct Loader',
  AUTO_DIVERT_STRAIGHTENER: 'Auto Divert Straightener',
  INBOUND_DOCK: 'Inbound Dock W/S',
  SORT_PS: 'Sort Problem Solve',
  BENCH: 'Bench',
  ADTA_STOW: 'ADTA Container Building',
  Pusher: 'Pusher',
  UTR_OPS_SUPERVISOR: 'UTR OPS Supervisor / SA',
  NON_CON_MANUAL_HANDLING: 'Non-Con Manual Handling',
  NON_SCAN_STOW: 'Non-Scan Stow',
  INBOUND_TRAFFIC_CONTROLLER: 'Inbound Traffic Controller',
  OUTBOUND_TRAFFIC_CONTROLLER: 'Outbound Traffic Controller',
  YARD_MARSHALL: 'Yard Marshall',
  INDUCT_LINE_LOADER: 'Induct Line Loader',
  PICK_AND_STAGE_PICK: 'Pick & Stage - Pick',
  PICK_STAGE_AND_DISPATCH_PROBLEM_SOLVE: 'Pick Stage & Dispatch Problem Solve',
  RTS: 'RTS',
  STOW_BAG_REPLENISHMENT: 'Stow Bag Replenishment',
  UTR_DISPATCH: 'UTR Dispatch',
  FIVE_S: '5S'
};

// Process cards whose per-slot labels (the small label above each employee icon) are
// inline-editable by planners. Matched against boardEntry.title. Add a card's title here to
// enable editable labels on it.
export const EDITABLE_SLOT_LABEL_CARDS = new Set([
  PROCESS_PATHS.DIVERTER,
  PROCESS_PATHS.AUTO_DIVERT_STRAIGHTENER,
  PROCESS_PATHS.INBOUND_DOCK,
  PROCESS_PATHS.SORT_PS,
  PROCESS_PATHS.STOW_BAG_REPLENISHMENT
]);

/**
 * Stamps each assignment with its slot position within its process path, in array order.
 *
 * Positions are only implied by array order in the data we receive, so they are made explicit once
 * here. Everything downstream reads allocationAttrs.slotOrdinal, which is what keeps an associate in
 * their slot when someone else is removed. Assignments that already carry an ordinal keep it.
 *
 * Ordinals already present are collected up front, so a freshly stamped one can never collide with an
 * existing ordinal that appears LATER in the list - e.g. [none, none, {slotOrdinal: 0}] stamps 1 and 2
 * rather than 0 and 1. A single counter would produce a duplicate 0 there. Mixed input like that does
 * not occur while the ordinal is stripped before save, but will once the model carries it: an old plan
 * has none, and one moved associate gets one.
 */
const withSlotOrdinals = (assignments) => {
  const takenByProcess = {};
  const takenFor = (process) => {
    if (!takenByProcess[process]) {
      takenByProcess[process] = new Set();
    }
    return takenByProcess[process];
  };

  (assignments || []).forEach((assignment) => {
    const attrs = assignment.allocationAttrs || {};
    if (Number.isInteger(attrs.slotOrdinal)) {
      takenFor(attrs.process).add(attrs.slotOrdinal);
    }
  });

  return (assignments || []).map((assignment) => {
    const attrs = assignment.allocationAttrs || {};
    if (Number.isInteger(attrs.slotOrdinal)) {
      return assignment;
    }
    const taken = takenFor(attrs.process);
    let ordinal = 0;
    while (taken.has(ordinal)) {
      ordinal += 1;
    }
    taken.add(ordinal);
    return { ...assignment, allocationAttrs: { ...attrs, slotOrdinal: ordinal } };
  });
};

/**
 * Removes slotOrdinal before the board is sent to the service.
 *
 * Slot positions are a website-side concern for now: the plan model has no field for them, so
 * sending one risks the update being rejected. Dropping it keeps the payload byte-identical to
 * before this change, at the cost of positions not surviving a reload until the model carries them.
 */
const withoutSlotOrdinals = (assignments) =>
  (assignments || []).map((assignment) => {
    const attrs = assignment.allocationAttrs || {};
    if (!Object.prototype.hasOwnProperty.call(attrs, 'slotOrdinal')) {
      return assignment;
    }
    const { slotOrdinal, ...rest } = attrs;
    return { ...assignment, allocationAttrs: rest };
  });

function RingStatus(color, lineType) {
  this.color = color;
  this.lineType = lineType;
}
export function ThumbnailBadge(color, text) {
  this.color = color;
  this.text = text;
}

function Associate(
  alias,
  name,
  img,
  trainedPaths,
  recommendedPaths,
  disallowedPaths,
  ppr,
  ringStatus,
  clockedInStatus,
  associateId,
  thumbnailBadge,
  trackingPath
) {
  this.alias = alias;
  this.name = name;
  this.img = img;
  this.trainedPaths = trainedPaths;
  this.recommendedPaths = recommendedPaths;
  this.disallowedPaths = disallowedPaths;
  this.ppr = ppr;
  this.ringStatus = ringStatus;
  this.clockedInStatus = clockedInStatus;
  this.associateId = associateId;
  this.thumbnailBadge = thumbnailBadge;
}

export const ALLOCATION_TYPE = {
  PROCESS: 'PROCESS',
  TENURE: 'TENURE',
  AISLES: 'AISLES',
  CLUSTERS: 'CLUSTERS'
};

export const PROCESS_TYPE = {
  INDUCT_LINE: 'INDUCT_LINE',
  ASL_LINE: 'ASL_LINE',
  GROUP: 'PROCESS_GROUP',
  PROCESS: 'PROCESS',
  SORT_CLUSTER: 'SORT_CLUSTER',
  ADTA_CLUSTER: 'ADTA_CLUSTER',
  TRAINING: 'TRAINING_GROUP'
};

// VLAB station allowlist — only used for EU/JP regions in prod.
// NA (us-east-1) is fully enabled for all stations via getRegion() === 'na' check in ShiftRotation.js.
// To onboard a new EU/JP station, add its station code here.
export const ALLOCATION_ALLOWED_STATION_CODES = ['DBI4', 'DBI7', 'DDN1', 'DLU2', 'DRM4', 'DSS2', 'DDD1'];

export const MAX_CLUSTER_CHUNK_SIZE = 5;

export const EMPTY_PROCESS = 'EMPTY_PROCESS';

export const TIME_WINDOW_PHASES = {
  INDUCT_AND_STOW: 'INDUCT_AND_STOW',
  PICK_AND_STAGE: 'PICK_AND_STAGE'
};

export const processAvailableCycles = (timeWindowAllocations) => {
  const result = new Set();

  timeWindowAllocations.forEach((timeWindowAllocation) => {
    result.add(timeWindowAllocation.timeWindowAttrs.find((attr) => !!attr.cycle).cycle);
  });

  return [...result];
};

export const processAvailableShifts = (timeWindowAllocations) => {
  const result = new Set();

  timeWindowAllocations.forEach((timeWindowAllocation) => {
    result.add(JSON.stringify(timeWindowAllocation.timeWindow));
  });

  return [...result].map((cycle) => JSON.parse(cycle));
};

export const processAvailableCycleShifts = (timeWindowAllocations) => {
  const cycleShiftsMap = new Map();
  timeWindowAllocations.forEach((timeWindowAllocation) => {
    const cycle = timeWindowAllocation.timeWindowAttrs[0].cycle;
    const newShiftEntries = [...(cycleShiftsMap.get(cycle) || []), timeWindowAllocation.timeWindow];
    cycleShiftsMap.set(cycle, newShiftEntries);
  });
  return cycleShiftsMap;
};

export const processAllocationData = (allocationBoard, associates, availableResourcesIds) => {
  const result = [];

  const associateInfo = {};

  associates.forEach((associate) => {
    if (!associateInfo[associate.id]) {
      associateInfo[associate.id] = new Associate(
        associate.id,
        associate.name,
        '',
        associate.trainedPaths,
        [associate.recommendedPath],
        associate.disallowedPaths,
        0,
        new RingStatus('Blue', 'solid'),
        false,
        associate.id
      );
      // Scan-To-Assign resolves a scanned badge locally against this. Sourced from the plan (bean),
      // not the enrichment poll, so it is present as soon as the board loads.
      associateInfo[associate.id].badgeBarcodeId = associate.badgeBarcodeId;
    }
  });

  const availableResourceSet = new Set(
    availableResourcesIds.filter((id) => {
      return !!associateInfo[id.toString()];
    })
  );

  allocationBoard.boardEntries.forEach((boardEntry) => {
    const assignments = allocationBoard.resourceAllocations.filter((resourceAllocation) => {
      return (
        resourceAllocation.boardEntity === boardEntry.id && availableResourceSet.has(resourceAllocation.resourceId)
      );
    });

    // removing the associates from the set to add the remaining
    // to the bench

    assignments.forEach((assignment) => {
      availableResourceSet.delete(assignment.resourceId);
    });
    result.push({
      ...boardEntry,
      assignments: withSlotOrdinals(assignments)
    });
  });

  const benchAssignments = [...availableResourceSet].map((resource) => {
    return {
      resourceId: resource,
      boardEntity: PROCESS_PATHS.BENCH,
      allocationAttrs: {
        process: PROCESS_PATHS.BENCH
      }
    };
  });

  result.push({
    id: PROCESS_PATHS.BENCH,
    title: 'Bench',
    type: PROCESS_PATHS.BENCH,
    assignments: benchAssignments,
    processPathEntries: []
  });

  // result.push({
  //   id: PROCESS_PATHS.FIVE_S,
  //   title: '5S',
  //   type: PROCESS_PATHS.FIVE_S,
  //   assignments: [],
  //   processPathEntries: []
  // });

  return {
    processInfo: result,
    associateInfo,
    boardEntries: allocationBoard.boardEntries
  };
};

export const processAssociates = (data, recommendations) => {
  const processedAssociates = data.map((item) => {
    const paths = Object.keys(item.processPathAttributeMap).sort();
    const recommendation = recommendations.find((rec) => rec.resource.resourceId === item.resource.resourceId);

    const disallowedPaths = [];
    const trainedPaths = [];

    paths.forEach((path) => {
      if (item.processPathAttributeMap[path].eligibility) {
        if (item.processPathAttributeMap[path].eligibility.ineligibleCriteria) {
          if (
            item.processPathAttributeMap[path].eligibility.ineligibleCriteria.filter(
              (c) => c.reason !== 'TRAINING_INCOMPLETE'
            ).length > 0
          ) {
            disallowedPaths.push(path);
          }
        }

        if (
          item.processPathAttributeMap[path].eligibility.trainingProfile &&
          item.processPathAttributeMap[path].eligibility.trainingProfile.completedTraining
        ) {
          trainedPaths.push(path);
        }
      }
    });

    return {
      name: nameFormatter(item.resource.resourceName, getRegion()),
      id: item.resource.resourceId,
      // Badge barcode is persisted on the ShiftPlan bean (stamped from PAS at plan build), so it is
      // available at plan load for Scan-To-Assign. Absent for associates with no badge on file.
      badgeBarcodeId: item.resource.badgeBarcodeId,
      disallowedPaths,
      riskRanking: item.riskRanking,
      trainedPaths,
      recommendedPath: recommendation && recommendation.recommendedProcess
    };
  });

  return processedAssociates.sort(alphabeticalSort('name'));
};

export const getTimeWindowAllocationKey = (timeWindowAllocation) => {
  const phase = timeWindowAllocation.timeWindowAttrs[0].phase;
  const cycle = timeWindowAllocation.timeWindowAttrs[0].cycle;
  const start = timeWindowAllocation.timeWindow.start;
  const end = timeWindowAllocation.timeWindow.end;
  return `${cycle}.${phase}.${start}.${end}`;
};

export const prepareModelForBackend = (fullModel, allocationBoards) => {
  const result = cloneDeep(fullModel);

  Object.keys(allocationBoards).forEach((boardKey) => {
    const [cycle, phase, start, end] = boardKey.split('.');
    const timeWindowAllocationIndex = fullModel.output.resourceAllocationOutput.timeWindowAllocations.findIndex(
      (twa) => {
        return (
          twa.timeWindowAttrs[0].phase === phase &&
          twa.timeWindowAttrs[0].cycle === cycle &&
          twa.timeWindow.start === start &&
          twa.timeWindow.end === end
        );
      }
    );

    const assignments = withoutSlotOrdinals(
      getUpdatedProcessInfoWithAssociatesToFiveSProcessPath(
        allocationBoards[boardKey].processInfo,
        allocationBoards[boardKey].associateInfo
      )
        .filter((process) => process.id !== PROCESS_PATHS.BENCH)
        .filter((process) => process.id !== PROCESS_PATHS.FIVE_S) // remove this line to enable 5S tracking to FCLM
        .reduce((acc, boardEntry) => {
          return acc.concat(boardEntry.assignments);
        }, [])
    );

    const newAllocationBoard = {
      ...fullModel.output.resourceAllocationOutput.timeWindowAllocations[timeWindowAllocationIndex].allocationBoard,
      boardEntries: allocationBoards[boardKey].boardEntries,
      resourceAllocations: assignments
    };

    result.output.resourceAllocationOutput.timeWindowAllocations[timeWindowAllocationIndex].allocationBoard =
      newAllocationBoard;

    result.input.sortLocationReservations = allocationBoards[boardKey].clusterDefinitions;
  });

  return result;
};
